import { ReactNode, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuthStore'
import { LoadingSpinner } from './LoadingSpinner'

// Helper to validate JWT and clear if invalid
const validateAndClearIfNeeded = (token: string | null): boolean => {
  console.log('[Auth Debug] Validating token:', token ? 'present' : 'missing')
  
  if (!token) {
    console.log('[Auth Debug] No token found')
    return false
  }
  
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      console.warn('[Auth Debug] Invalid JWT format - not 3 parts')
      localStorage.removeItem('auth-storage')
      return false
    }
    
    const payload = JSON.parse(atob(parts[1]))
    console.log('[Auth Debug] Token payload:', payload)
    
    // Clear old mock tokens - but be more lenient in development
    if (payload.name === 'John Doe' && import.meta.env.PROD) {
      console.warn('[Auth Debug] Clearing old mock token in production')
      localStorage.removeItem('auth-storage')
      return false
    }
    
    // Clear expired tokens with some grace period
    if (payload.exp) {
      const now = Date.now() / 1000
      const timeUntilExpiry = payload.exp - now
      console.log('[Auth Debug] Token expires in:', timeUntilExpiry, 'seconds')
      
      if (timeUntilExpiry < -300) { // 5 minute grace period
        console.warn('[Auth Debug] Clearing expired token (grace period exceeded)')
        localStorage.removeItem('auth-storage')
        return false
      }
    }
    
    console.log('[Auth Debug] Token validation passed')
    return true
  } catch (e) {
    console.warn('[Auth Debug] Clearing malformed token:', e)
    localStorage.removeItem('auth-storage')
    return false
  }
}

interface ProtectedRouteProps {
  children: ReactNode
  allowedUserTypes?: ('parent' | 'admin')[]
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedUserTypes = ['parent', 'admin'] 
}) => {
  const { user, token, isLoading, logout } = useAuthStore()
  const location = useLocation()
  
  // Validate token on component mount
  useEffect(() => {
    if (token && !validateAndClearIfNeeded(token)) {
      logout() // Force logout if token is invalid
    }
  }, [token, logout])
  
  // Show loading while checking authentication state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="large" message="กำลังตรวจสอบสิทธิ์..." />
      </div>
    )
  }
  
  // Validate token before proceeding
  if (token && !validateAndClearIfNeeded(token)) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  
  // Redirect to login if not authenticated
  if (!user || !token) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  
  // Check if user type is allowed for this route
  if (!allowedUserTypes.includes(user.type)) {
    // Redirect to appropriate dashboard based on user type
    const redirectPath = user.type === 'admin' ? '/admin/dashboard' : '/dashboard'
    return <Navigate to={redirectPath} replace />
  }
  
  // User is authenticated and authorized
  return <>{children}</>
}

// Convenience components for specific user types
export const ParentRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute allowedUserTypes={['parent']}>
    {children}
  </ProtectedRoute>
)

export const AdminRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute allowedUserTypes={['admin']}>
    {children}
  </ProtectedRoute>
)